// Lightweight SEO head manager without adding react-helmet.
// Updates document.title and the standard meta tags (description, og:*)
// so that crawlers (Google Search Console, Open Graph previews) pick up
// per-page metadata after client-side hydration.
const PROPERTY_TAGS = new Set(['og:title', 'og:description', 'og:image', 'og:url', 'og:type']);

// Fallback OG image used when a page does not supply one. Keeps social-media
// previews consistent (LinkedIn / Slack / WhatsApp) even for materials without
// an attached photo.
const DEFAULT_OG_IMAGE = 'https://customer-assets.emergentagent.com/job_ee642069-4fc1-425d-bfbc-e67ee20a2646/artifacts/vxtmarer_ChatGPT%20Image%202.%20M%C3%A4rz%202026%2C%2012_35_03.png';

function upsertMetaTag(name, content) {
  if (typeof document === 'undefined' || content == null) return;
  const isProperty = PROPERTY_TAGS.has(name);
  const selector = isProperty ? `meta[property="${name}"]` : `meta[name="${name}"]`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    if (isProperty) el.setAttribute('property', name);
    else el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', String(content));
}

function upsertLinkCanonical(href) {
  if (typeof document === 'undefined' || !href) return;
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Set page SEO head tags. Call from within useEffect on mount/update.
 * Returns a cleanup function that resets title if desired.
 */
export function setPageSeo({ title, description, image, url, type = 'website' }) {
  if (typeof document === 'undefined') return () => {};
  const previousTitle = document.title;
  if (title) document.title = title;
  if (description) upsertMetaTag('description', description);
  if (title) upsertMetaTag('og:title', title);
  if (description) upsertMetaTag('og:description', description);
  upsertMetaTag('og:image', image || DEFAULT_OG_IMAGE);
  if (url) {
    upsertMetaTag('og:url', url);
    upsertLinkCanonical(url);
  }
  upsertMetaTag('og:type', type);

  return () => {
    if (title) document.title = previousTitle;
  };
}

/**
 * Inject or update a JSON-LD <script type="application/ld+json"> block in the
 * document head. `id` identifies the block so it can be updated/cleaned up.
 * Returns a cleanup function that removes the script on unmount.
 */
export function setJsonLd(id, data) {
  if (typeof document === 'undefined' || !id) return () => {};
  let el = document.head.querySelector(`script[type="application/ld+json"][data-jsonld-id="${id}"]`);
  if (!data) {
    if (el) el.remove();
    return () => {};
  }
  if (!el) {
    el = document.createElement('script');
    el.setAttribute('type', 'application/ld+json');
    el.setAttribute('data-jsonld-id', id);
    document.head.appendChild(el);
  }
  try {
    el.textContent = JSON.stringify(data);
  } catch {
    // ignore serialization errors
  }
  return () => {
    const cur = document.head.querySelector(`script[type="application/ld+json"][data-jsonld-id="${id}"]`);
    if (cur) cur.remove();
  };
}
